'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  hydratedArtifactMarkdown,
  hydratedEvidenceMarkdown,
} from '@/components/mobile-citations/citation-evidence';
import {
  hydrateEvidence,
  type MobileUiConfigArtifact,
  type MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  citationTableColumns,
  filterCitationRows,
  populatedColumnFields,
  sortCitationRows,
} from '@/shared/mobile-service/lib/citation-table';
import { resolveNestedColumns } from '@/shared/mobile-service/lib/columns';
import { artifactDisplayName } from '@/shared/mobile-service/lib/labels';
import type { ColumnFilterValue, FilterState } from '@/shared/mobile-service/lib/where';
import { ColumnSelector } from '../ColumnSelector';
import { DataTable, type NestedTableConfig } from '../DataTable';
import { CitedRowMarkdownPane } from './CitedRowMarkdownPane';

const DEFAULT_LIMIT = 50;

/**
 * Citation-pane table: the same columns as the mobile viewer, over hydrate rows.
 *
 * Unlike {@link TableView} this has no GraphQL of its own. When ``total`` and
 * ``onPageChange`` are provided, paging is server-backed; sort and filter
 * still apply to the current page.
 */
export function CitedTable({
  rows,
  artifact,
  uiConfig,
  mobileAcquisitionId,
  focusIds,
  viewerHref,
  label,
  total,
  offset,
  onPageChange,
}: {
  rows: Record<string, unknown>[];
  artifact: MobileUiConfigArtifact;
  uiConfig: MobileUiConfigResponse;
  mobileAcquisitionId: number;
  focusIds: Array<string | number>;
  viewerHref: string | null;
  label?: string | null;
  total?: number;
  offset?: number;
  onPageChange?: (page: { offset: number; limit: number }) => void;
}) {
  const t = useTimePilotT();
  const resolved = useMemo(() => citationTableColumns(artifact, uiConfig), [artifact, uiConfig]);
  const [localOffset, setLocalOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<FilterState>({});
  const [visibleFields, setVisibleFields] = useState<string[]>(
    resolved?.defaultVisibleFields ?? [],
  );
  const [selectedId, setSelectedId] = useState<string | null>(
    focusIds[0] != null ? String(focusIds[0]) : null,
  );
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(null);
  const [detailMarkdown, setDetailMarkdown] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<Error | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const { cellId, accessToken, enabled: sessionEnabled } = useCellServiceSession();

  const defaultVisibleKey = resolved?.defaultVisibleFields.join('\0') ?? '';
  const columnsTouched = useRef(false);
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  const limitRef = useRef(limit);
  limitRef.current = limit;

  useEffect(() => {
    columnsTouched.current = false;
  }, [artifact.graphql_type, defaultVisibleKey]);

  useEffect(() => {
    if (!resolved || columnsTouched.current) return;
    setVisibleFields(populatedColumnFields(resolved.defaultVisibleFields, rows));
  }, [artifact.graphql_type, defaultVisibleKey, resolved, rows]);

  useEffect(() => {
    setSelectedId(focusIds[0] != null ? String(focusIds[0]) : null);
  }, [focusIds]);

  const filtersKey = JSON.stringify(filters);
  const sortKey = sort ? `${sort.field}:${sort.direction}` : '';
  const pageResetReady = useRef(false);
  useEffect(() => {
    if (!pageResetReady.current) {
      pageResetReady.current = true;
      return;
    }
    setLocalOffset(0);
    onPageChangeRef.current?.({ offset: 0, limit: limitRef.current });
  }, [filtersKey, sortKey]);

  const visibleColumns = useMemo(() => {
    if (!resolved) return [];
    const visible = new Set(visibleFields);
    return resolved.columns.filter((column) => visible.has(column.field));
  }, [resolved, visibleFields]);

  const detailFields = useMemo(() => {
    if (!resolved) return [];
    const seen = new Set<string>();
    const fields = [];
    for (const column of [...resolved.columns, ...resolved.contentFields]) {
      if (seen.has(column.field)) continue;
      seen.add(column.field);
      fields.push(column);
    }
    return fields;
  }, [resolved]);

  const nestedConfig = useMemo<Record<string, NestedTableConfig>>(() => {
    const map: Record<string, NestedTableConfig> = {};
    for (const column of visibleColumns) {
      if (column.render === 'nested_table' && column.nested) {
        map[column.field] = {
          label: column.label,
          columns: resolveNestedColumns(column.nested, uiConfig),
        };
      }
    }
    return map;
  }, [visibleColumns, uiConfig]);

  const filtered = useMemo(() => filterCitationRows(rows, filters), [rows, filters]);
  const sorted = useMemo(() => sortCitationRows(filtered, sort), [filtered, sort]);
  const serverPaging = onPageChange != null && total != null;
  const reportPage = (nextOffset: number, nextLimit = limit) => {
    setLocalOffset(nextOffset);
    onPageChange?.({ offset: nextOffset, limit: nextLimit });
  };
  const pageOffset = serverPaging ? (offset ?? 0) : localOffset;
  const page = useMemo(
    () => (serverPaging ? sorted : sorted.slice(pageOffset, pageOffset + limit)),
    [sorted, serverPaging, pageOffset, limit],
  );
  const sourceLabel = label?.trim() || artifactDisplayName(artifact, 'Table');
  const rowCount = serverPaging ? total : sorted.length;
  const subtitle = `${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`;
  const openRow = (id: string) => {
    setSelectedId(id);
    const match = rows.find((row) => String(row[resolved?.idField ?? '']) === id) ?? null;
    setDetailRow(match);
    setDetailMarkdown(null);
    setDetailError(null);
    setDetailLoading(match != null);
  };

  useEffect(() => {
    if (!detailRow || !selectedId || !sessionEnabled || !cellId || !accessToken) return;

    const numericId = Number(selectedId);
    if (!Number.isFinite(numericId)) {
      setDetailError(new Error('Invalid artifact id'));
      return;
    }

    const controller = new AbortController();
    setDetailLoading(true);
    setDetailError(null);

    void hydrateEvidence({
      cellId,
      accessToken,
      locator: {
        kind: 'artifact',
        mobileAcquisitionId,
        mobileArtifactId: numericId,
        graphqlType: artifact.graphql_type,
        endpoint: artifact.endpoint,
        queryName: artifact.front_end?.graphql_root_query,
        requestedView: 'markdown',
      },
      limit: 1,
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        const item = response.items[0];
        setDetailMarkdown(
          (item ? hydratedEvidenceMarkdown(item) : null) ??
            hydratedArtifactMarkdown(item ?? detailRow, sourceLabel),
        );
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setDetailError(error instanceof Error ? error : new Error(String(error)));
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });

    return () => controller.abort();
  }, [
    accessToken,
    artifact.endpoint,
    artifact.front_end?.graphql_root_query,
    artifact.graphql_type,
    cellId,
    detailReloadKey,
    detailRow,
    mobileAcquisitionId,
    selectedId,
    sessionEnabled,
    sourceLabel,
  ]);

  if (!resolved) return null;

  return (
    <div
      data-testid="CitationCitedTable"
      className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden rounded-md bg-white/5"
    >
      <header className="flex min-w-0 shrink-0 items-center justify-between gap-3 border-b border-amber-400/40 bg-amber-400/10 px-3 py-2">
        <div className="min-w-0">
          <p className="m-0 truncate text-sm text-amber-100">{sourceLabel}</p>
          <p className="m-0 text-xs text-amber-100/70">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <ColumnSelector
            columns={resolved.columns}
            visibleFields={visibleFields}
            onChange={(next) => {
              columnsTouched.current = true;
              setVisibleFields(next);
              if (sort && !next.includes(sort.field)) setSort(null);
              reportPage(0);
            }}
          />
          {viewerHref ? (
            <a
              href={viewerHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-white/80 underline decoration-white/40 underline-offset-2 hover:text-white"
            >{t("viewers.openInMobileViewer")}</a>
          ) : null}
        </div>
      </header>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2">
        <DataTable
          columns={visibleColumns}
          rows={page}
          idField={resolved.idField}
          total={rowCount}
          offset={pageOffset}
          limit={limit}
          onOffsetChange={(next) => reportPage(next)}
          onLimitChange={(next) => {
            setLimit(next);
            reportPage(0, next);
          }}
          sort={sort}
          onSortChange={setSort}
          filters={filters}
          onFilterChange={(field, value) => {
            setFilters((current) => {
              const next = { ...current };
              if (value === undefined) delete next[field];
              else next[field] = value as ColumnFilterValue;
              return next;
            });
          }}
          isFetching={false}
          nested={nestedConfig}
          contentFields={detailFields}
          contentTitle={(row) => `${artifactDisplayName(artifact)} #${row[resolved.idField] ?? ''}`}
          selectedId={selectedId}
          onSelectRow={openRow}
          fillHeight
        />
      </div>
      {detailRow ? (
        <CitedRowMarkdownPane
          markdown={detailMarkdown}
          isLoading={detailLoading || !sessionEnabled}
          error={detailError}
          title={`${artifactDisplayName(artifact)} #${detailRow[resolved.idField] ?? ''}`}
          onRetry={() => setDetailReloadKey((key) => key + 1)}
          onClose={() => setDetailRow(null)}
        />
      ) : null}
    </div>
  );
}
